import type { AbortUploadResponse, CreateUploadResponse } from "../api/client";

/** Phases the panel shows while an upload is in flight. */
export type UploadPhase = "requesting" | "uploading";

/**
 * The slice of `ApiClient` an upload needs. Narrow so the flow can be tested
 * without a token provider or a fetch.
 */
export interface UploadApi {
  createUpload(file: {
    name: string;
    size: number;
    folderId?: string | undefined;
  }): Promise<CreateUploadResponse>;
  uploadToS3(uploadUrl: string, file: Blob): Promise<void>;
  abortUpload(contractId: string): Promise<AbortUploadResponse>;
}

/**
 * The upload failed and the row it left behind could not be removed either.
 *
 * Kept distinct from the upload failure because the two mean different things
 * to the person at the screen: one says "nothing was filed", this one says
 * "something was filed and it should not have been".
 */
export class UploadNotCancelledError extends Error {
  readonly contractId: string;
  readonly uploadError: unknown;
  readonly abortError: unknown;

  constructor(contractId: string, uploadError: unknown, abortError: unknown) {
    super(`upload of contract ${contractId} failed and could not be cancelled`);
    this.name = "UploadNotCancelledError";
    this.contractId = contractId;
    this.uploadError = uploadError;
    this.abortError = abortError;
  }
}

/**
 * Two requests the person sees as one: the API writes the ledger row and signs
 * a URL, then the browser PUTs the bytes to S3 with it.
 *
 * The row has to exist first — the extractor runs off the S3 event and needs
 * somewhere to attach the PDF — so a PUT that fails leaves a contract nobody
 * uploaded. Cancelling it here is what keeps a failed upload out of the
 * ledger.
 */
export async function runUpload(
  api: UploadApi,
  file: File,
  onPhase: (phase: UploadPhase) => void,
  /** Undefined leaves the contract unfiled, which the screens show as 未分類. */
  folderId?: string | undefined,
): Promise<void> {
  onPhase("requesting");
  const started = await api.createUpload({
    name: file.name,
    size: file.size,
    ...(folderId === undefined ? {} : { folderId }),
  });

  onPhase("uploading");
  try {
    await api.uploadToS3(started.uploadUrl, file);
  } catch (uploadError) {
    try {
      await api.abortUpload(started.contractId);
    } catch (abortError) {
      throw new UploadNotCancelledError(
        started.contractId,
        uploadError,
        abortError,
      );
    }
    throw uploadError;
  }
}
